import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Select,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useClientContext } from "../context/ClientContext";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import { ReactComponent as BNI_LOGO } from "../assets/BNI_logo.svg";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { clientGroups } from "../assets/clientData";
import { useAuth } from "../context/AuthContextProvider";
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import PsychologyIcon from '@mui/icons-material/Psychology';

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const isClientInsights = location.pathname === "/clientInsights";
  const currentDate = new Date();
  const formattedDate = `${currentDate.toLocaleDateString("en-GB", {
    weekday: "long"
  })}, ${currentDate.getDate()} ${currentDate.toLocaleDateString("en-GB", {
    month: "short"
  })} ,${currentDate.getFullYear()}`;

  const { clientName, setClientName, clientGroup, setClientGroup } =
    useClientContext();
  const { username } = useAuth();
  const [availableCompanies, setAvailableCompanies] = useState([]);

  // Hide dropdowns on landing page
  const hideDropdowns = ["/landing", "/notifications"];
  const showDropdowns = !hideDropdowns.includes(location.pathname);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Update available companies when group changes
  useEffect(() => {
    const selectedGroupData = clientGroups.find(
      (group) => group.group_name === clientGroup
    );
    if (selectedGroupData) {
      const companies = Object.values(selectedGroupData.company_names);
      setAvailableCompanies(companies);
      // Reset client selection if current selection isn't in the new group
      if (!companies.includes(clientName)) {
        setClientName(companies[0]);
      }
    }
  }, [clientGroup, clientName, setClientName]);

  const handleClientChange = (event) => {
    setClientName(event.target.value);
  };

  const handleClientGroupChange = (event) => {
    setClientGroup(event.target.value);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    navigate("/");
  };

  return (
    <AppBar position="static" sx={{ backgroundColor: "white", height: "10vh" }}>
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
        }}
      >
        <div
          className="flex items-center justify-start gap-12 cursor-pointer"
          onClick={() => {
            navigate("/landing");
          }}
        >
          {/* Page Title on the Left */}
          { /*<img src={BNI_LOGO} alt="BNI Logo" /> */}
          {/*<BNI_LOGO /> */}
        </div>
        {/* Client selections - only show if not on landing page */}
        {showDropdowns && (
          <div className="flex items-center gap-8">
            {/* Client Group dropdown */}
            <div className="flex items-center gap-3">
              <div
                className="text-xs font-semibold tracking-wide"
                style={{ color: "#6C757D" }}
              >
                Client Group
              </div>
              <FormControl
                variant="standard"
                sx={{
                  minWidth: 120,
                  "& .MuiInput-root": {
                    "&:before": {
                      borderBottom: "1px solid #00555C",
                    },
                    "&:hover:not(.Mui-disabled):before": {
                      borderBottom: "2px solid #00555C",
                    },
                    "&.Mui-focused:after": {
                      borderBottom: "2px solid #00555C",
                    },
                  },
                }}
              >
                <Select
                  value={clientGroup}
                  onChange={handleClientGroupChange}
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#00555C",
                    "& .MuiSelect-select": {
                      paddingY: 0.5,
                    },
                    "& .MuiSelect-icon": {
                      color: "#00555C",
                    },
                  }}
                  IconComponent={ExpandMoreIcon}
                >
                  {clientGroups.map((group) => (
                    <MenuItem key={group.group_name} value={group.group_name}>
                      {group.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            {/* Client dropdown */}
            <div className="flex items-center gap-3">
              <div
                className="text-xs font-semibold tracking-wide"
                style={{ color: "#6C757D" }}
              >
                Client Name
              </div>
              <FormControl
                variant="standard"
                sx={{
                  minWidth: 120,
                  "& .MuiInput-root": {
                    "&:before": {
                      borderBottom: "1px solid #00555C",
                    },
                    "&:hover:not(.Mui-disabled):before": {
                      borderBottom: "2px solid #00555C",
                    },
                    "&.Mui-focused:after": {
                      borderBottom: "2px solid #00555C",
                    },
                  },
                }}
              >
                <Select
                  value={clientName}
                  onChange={handleClientChange}
                  sx={{
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    color: "#00555C",
                    "& .MuiSelect-select": {
                      paddingY: 0.5,
                    },
                    "& .MuiSelect-icon": {
                      color: "#00555C",
                    },
                  }}
                  IconComponent={ExpandMoreIcon}
                >
                  {availableCompanies.map((company) => (
                    <MenuItem key={company} value={company}>
                      {company}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        )}
        {/* User section with icons */}
        <div className="flex items-center justify-start">
        <IconButton
            edge="end"
            sx={{ color: "#00555C" }}
            size="large"
            onClick={() => { navigate("/agentic") }}
          >
           <PsychologyIcon />
          </IconButton>
        <IconButton
            edge="end"
            sx={{ color: "#00555C" }}
            size="large"
            onClick={() => {}}
          >
           <HelpOutlineOutlinedIcon />
          </IconButton>
          <IconButton
            edge="end"
            sx={{ color: "#00555C" }}
            size="large"
            onClick={() => {
              navigate("/landing");
            }}
          >
            <HomeOutlinedIcon />
          </IconButton>
          <IconButton
            edge="end"
            sx={{ color: "#00555C" }}
            size="large"
            onClick={() => navigate("/notifications")}
          >
            <NotificationsOutlinedIcon />
          </IconButton>
          <div className="pl-5 ">
            <div className="text-sm text-main">{username}</div>
            <div className="text-xs text-main">{formattedDate}</div>
          </div>
          <IconButton edge="end" sx={{ color: "#00555C" }} size="large"  onClick={handleMenuClick}>
            <AccountCircleIcon />
          </IconButton>
          <Menu
        id="chatbot-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        sx={{
          "& .MuiMenu-paper": {
            minWidth: "100px"
          }
        }}
      >
        <MenuItem onClick={handleLogout}>
          <span className="text-xs">
            <i>Logout</i>​
          </span>
        </MenuItem>
      </Menu>
        </div>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
